import React, { useState, useEffect, useContext, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import AppsIcon from "@mui/icons-material/Apps";
import PropTypes from "prop-types";
import { Avatar, Box, Tooltip, Drawer } from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import PoetLogo from "assets/images/POET Logo.jpg";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import styled from "@emotion/styled";
// import { UserContext } from 'context';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { useUser, useAuthentication } from "hooks/useUser";
import useFetchHealthPlan from "hooks/useFetchHealthPlan";
import { useSelector } from "react-redux";
import { FeatureFlagContext } from "context";
import { postUserActivity } from "components/shared/postUserActivity";
import { TrackingLink } from "components/shared/TrackingLink";
import { userHasAllowedGroup, userHasRuleBuilderAccess } from "utils/shared";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const StyledAppBar = styled(AppBar)(({ theme }) => {
  return {
    backgroundColor: "#ffffff",
    color: "#392AB6",
    zIndex: theme.zIndex.drawer + 1,
  };
});
const StyledBox = styled(Box)({
  flexGrow: 1,
  justifyContent: "end",
  alignItems: "center",
  display: "flex",
});

export default function MenuAppBar({ onLogout, lastLoginTime }) {
  const featureFlags = useContext(FeatureFlagContext);

  const hp = useSelector((state) => state.healthPlan.healthPlan);
  const [hpWithImg, fetchHealthPlan] = useFetchHealthPlan();

  useEffect(() => {
    if (hp) fetchHealthPlan(hp.healthPlanName);
  }, [hp, fetchHealthPlan]);

  const [user] = useUser();
  const formattedLastLoginTime = lastLoginTime
    ? dayjs
        .utc(lastLoginTime, "YYYY-MM-DD HH:mm:ss")
        .local()
        .format("MM/DD/YYYY HH:mm")
    : "";

  const authenticated = !!user;
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openPolicyOperations, setOpenPolicyOperations] = useState(true);
  const [openConfiguration, setOpenConfiguration] = useState(true);
  const toggleDrawer = () => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenDrawer(!openDrawer);
  };

  const logOutApplication = async () => {
    try {
      await postUserActivity(
        null,
        null,
        0,
        "N/A",
        "click",
        "NavBar",
        "Logout",
        "Logout"
      );
    } catch (error) {
      console.log("Failed to post user activity:", error);
    }
    onLogout();
  };

  if (!featureFlags || Object.keys(featureFlags).length === 0) {
    return null;
  }

  const handlePolicyOperations = (e) => {
    setOpenPolicyOperations(!openPolicyOperations);
    e.stopPropagation();
    e.preventDefault();
  };

  const handleConfiguration = (e) => {
    setOpenConfiguration(!openConfiguration);
    e.stopPropagation();
    e.preventDefault();
  };

  const navItems = [
    {
      text: "Dashboard",
      link: "/dashboard",
      subMenu: [],
      collapseFunction: null,
      collapseState: null,
      divider: true,
      selected: false,
    },
    {
      text: "Policy Operations",
      link: "/policy-operations",
      collapseFunction: handlePolicyOperations,
      collapseState: openPolicyOperations,
      selected: false,
      divider: openPolicyOperations ? false : true,
      subMenu: [
        {
          text: "Adoption History",
          link: "/policy-history",
          divider: false,
          selected: false,
        },
        {
          text: "Enforcement Rules",
          link: "/policies-active",
          divider: false,
          selected: false,
          state: { pageType: "Active" },
        },
        {
          text: "Exclusions",
          link: "/exclusions",
          divider: false,
          selected: false,
        },
        {
          text: "Groups - Diagnosis",
          link: "/diagnosis-groups",
          divider: false,
          selected: false,
        },
        {
          text: "Groups - Procedures",
          link: "/procedure-groups",
          divider: true,
          selected: false,
        },
      ],
    },
    {
      text: "Release Management",
      link: "/release-plan",
      subMenu: [],
      collapseFunction: null,
      collapseState: null,
      divider: true,
      selected: false,
    },
    {
      text: "Configuration",
      link: "/configuration",
      collapseFunction: handleConfiguration,
      collapseState: openConfiguration,
      selected: false,
      divider: openConfiguration ? false : true,
      subMenu: [
        {
          text: "Health Plan",
          link: "/hp-config",
          divider: false,
          selected: false,
        },
        {
          text: "Policy",
          link: "/policy-config",
          divider: false,
          selected: false,
        },
        userHasRuleBuilderAccess(user) && {
          text: "Rule Builder",
          link: "/rule-builder",
          divider: true,
          selected: false,
        },
      ].filter(Boolean),
    },
  ];

  const list = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer()}
      onKeyDown={toggleDrawer()}
    >
      <List>
        {navItems.map((navItem) =>
          navItem.subMenu.length > 0 ? (
            <div key={navItem.text}>
              <ListItem disablePadding>
                <TrackingLink
                  to={navItem.link}
                  state={{ pageType: navItem?.state?.pageType }}
                  activityProps={[
                    null,
                    null,
                    0,
                    "N/A",
                    "click",
                    "NavBar",
                    "Navigation to",
                    navItem.text,
                  ]}
                >
                  <ListItemButton
                    selected={navItem.selected}
                    divider={navItem.divider}
                    sx={{ width: 250 }}
                    onClick={navItem.collapseFunction}
                  >
                    <ListItemText primary={navItem.text} />
                    {navItem.collapseState ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </ListItemButton>
                </TrackingLink>
              </ListItem>
              {navItem.subMenu.map((subMenu) => (
                <Collapse
                  key={subMenu.text}
                  in={navItem.collapseState}
                  timeout="auto"
                  unmountOnExit
                >
                  <ListItem disablePadding>
                    <TrackingLink
                      to={subMenu.link}
                      state={{ pageType: subMenu?.state?.pageType }}
                      activityProps={[
                        null,
                        null,
                        0,
                        "N/A",
                        "click",
                        "NavBar",
                        "Navigation to",
                        subMenu.text,
                      ]}
                    >
                      <ListItemButton
                        selected={subMenu.selected}
                        divider={subMenu.divider}
                        sx={{
                          paddingLeft: 4,
                          width: 250,
                        }}
                      >
                        <ListItemText primary={subMenu.text} />
                      </ListItemButton>
                    </TrackingLink>
                  </ListItem>
                </Collapse>
              ))}
            </div>
          ) : (
            <ListItem key={navItem.text} disablePadding>
              <TrackingLink
                activityProps={[
                  null,
                  null,
                  0,
                  "N/A",
                  "click",
                  "NavBar",
                  "Navigation to",
                  navItem.text,
                ]}
                to={navItem.link}
              >
                <ListItemButton
                  sx={{ width: 250 }}
                  selected={navItem.selected}
                  divider={navItem.divider}
                >
                  <ListItemText primary={navItem.text} />
                </ListItemButton>
              </TrackingLink>
            </ListItem>
          )
        )}
      </List>
    </Box>
  );

  return (
    <StyledAppBar>
      <Drawer open={openDrawer} onClose={toggleDrawer()}>
        <Toolbar />
        {list()}
      </Drawer>
      <Toolbar
        sx={{
          minHeight: "40px !important",
        }}
      >
        {authenticated && (
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={toggleDrawer()}
          >
            <AppsIcon />
          </IconButton>
        )}
        <StyledBox sx={{ justifyContent: "start !important" }}>
          <img src={PoetLogo} style={{ height: "50px", marginRight: "20px" }} />
          {hp && hpWithImg?.logoInByte && (
            <img
              src={`data:image/jpeg;base64,${hpWithImg.logoInByte}`}
              style={{ height: "50px" }}
            />
          )}
        </StyledBox>

        {authenticated && (
          <StyledBox>
            <Tooltip
              title={
                <div style={{ whiteSpace: "pre-line" }}>
                  {`${user.given_name || user.First_Name} ${user.family_name || user.Last_Name}\n${user.email}\n${user.groups.join("\n")}${formattedLastLoginTime ? `\nLast Login: ${formattedLastLoginTime}` : ""}`}
                </div>
              }
            >
              <Avatar sx={{ bgcolor: deepOrange[500] }}>
                {user.given_name ? user.given_name[0] : ""}
                {user.family_name ? user.family_name[0] : ""}
              </Avatar>
            </Tooltip>
            <Tooltip title="User Settings">
              <IconButton
                size="large"
                aria-label="user settings"
                aria-haspopup="menu"
                aria-controls="settings-menu"
                color="inherit"
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton
                size="large"
                aria-label="logout"
                color="inherit"
                onClick={logOutApplication}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </StyledBox>
        )}
      </Toolbar>
    </StyledAppBar>
  );
}

MenuAppBar.propTypes = {
  onLogout: PropTypes.func,
  lastLoginTime: PropTypes.string,
};
